import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogConfig } from '@angular/material/dialog';
import { GuardsCheckEnd, NavigationEnd, Router } from '@angular/router';
import { ErrorAlertComponent, GlobalComponentService, IGlobalComponent, IndeterminateProgressBarComponent, InfoAlertComponent, InfoConfirmComponent, TmModalCloseReason, TmModalData } from '@techmore/global';
import { merge } from 'rxjs';
import { distinctUntilChanged, filter, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { IS_BROWSER_TOKEN } from './shared/token/is_browser.token';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements IGlobalComponent, OnInit {
  @ViewChild(InfoAlertComponent, { static: false }) infoAlert: InfoAlertComponent;
  @ViewChild(InfoConfirmComponent, { static: false }) infoConfirm: InfoConfirmComponent;
  @ViewChild(ErrorAlertComponent, { static: false }) errorAlert: ErrorAlertComponent;
  @ViewChild(IndeterminateProgressBarComponent, { static: false }) indeterminateProgressBar: IndeterminateProgressBarComponent;
  isBrowser = inject(IS_BROWSER_TOKEN);
  defaultInfoTitle = '系統提示';
  defaultInfoAlertTitle = '系統提示';
  defaultErrorAlertTitle = '警告';
  modalConfig: MatDialogConfig<TmModalData> = { data: { cancelBtnLabel: '', showClose: true, title: '系統提示', okBtnLabel: '確定' }, role: 'alertdialog', hasBackdrop: false };
  confirmModalConfig: MatDialogConfig<TmModalData> = { data: { cancelBtnLabel: '取消', showClose: true, title: '系統提示', okBtnLabel: '確定' }, role: 'alertdialog', hasBackdrop: false };

  errorModalConfig: MatDialogConfig<TmModalData> = { data: { cancelBtnLabel: '', showClose: true, title: '警告', okBtnLabel: '確定' }, role: 'alertdialog', hasBackdrop: false };

  constructor(
    private globalComponentService: GlobalComponentService,
    private router: Router,
  ) {
    const isUrlPathChange$ = this.router.events.pipe(
      filter((evt): evt is GuardsCheckEnd => evt instanceof GuardsCheckEnd),
      map(evt => {
        const [formUrl, formUrlQuery] = this.router.url?.split('?') ?? [null, null];
        const [targetUrl, targetUrlQuery] = evt.url?.split('?') ?? [null, null];
        return formUrl !== targetUrl;
      }),
      distinctUntilChanged(),
    );
    this.router.events.pipe(
      filter((evt): evt is NavigationEnd => evt instanceof NavigationEnd),
      withLatestFrom(isUrlPathChange$),
      tap(console.log),
      takeUntilDestroyed(),
    ).subscribe(([evt, isUrlPathChange]) => {
      if (isUrlPathChange) {
        window.scrollTo(0, 0);
      }
    });
  }

  ngOnInit(): void {
    this.globalComponentService.globalCmp = this;
  }

  openInfoAlert(message: string, param?: { title?: string, afterClose?: () => void }) {
    this.infoAlert!.message = message;
    if (!this.isBrowser) { return this.infoAlert as InfoAlertComponent; }
    if (param) {
      this.infoAlert!.title = param.title || this.defaultInfoAlertTitle;
      if (param.afterClose) {
        let closeReason: TmModalCloseReason = TmModalCloseReason.SUBMIT;
        merge(
          this.infoAlert!.afterConfirm.pipe(tap(() => { closeReason = TmModalCloseReason.SUBMIT })),
          this.infoAlert!.afterCancel.pipe(tap(() => { closeReason = TmModalCloseReason.CLOSE })),
        ).pipe(take(1))
          .subscribe(() => {
            (param.afterClose as () => void)();
          });
      }
    }

    this.infoAlert!.open();
    return this.infoAlert as InfoAlertComponent;
  }

  openInfoConfirm(message: string, param?: { title?: string, afterConfirm?: () => void, afterClose?: () => void }) {
    this.infoConfirm!.message = message;
    if (!this.isBrowser) { return this.infoConfirm as InfoConfirmComponent; }
    if (param) {
      this.infoConfirm!.title = param.title || this.defaultInfoTitle;
      if (param.afterConfirm || param.afterClose) {
        let closeReason: TmModalCloseReason = TmModalCloseReason.SUBMIT;
        merge(
          this.infoConfirm!.afterConfirm.pipe(tap(() => { closeReason = TmModalCloseReason.SUBMIT })),
          this.infoConfirm!.afterCancel.pipe(tap(() => { closeReason = TmModalCloseReason.CLOSE })),
        ).pipe(take(1))
          .subscribe(() => {
            if (closeReason === TmModalCloseReason.SUBMIT) {
              (param.afterConfirm as () => void)();
            } else {
              (param.afterClose as () => void)();
            }
          });
      }
    }

    this.infoConfirm!.open();
    return this.infoConfirm as InfoConfirmComponent;
  }

  openErrorAlert(message: string, param?: { title?: string, afterConfirm?: () => void, afterClose?: () => void, showCopyTip?: boolean }) {
    this.errorAlert!.message = message;
    if (!this.isBrowser) { return this.errorAlert as ErrorAlertComponent; }
    if (param) {
      this.errorAlert!.title = param.title || this.defaultErrorAlertTitle;
      let closeReason: TmModalCloseReason = TmModalCloseReason.SUBMIT;
      merge(
        this.errorAlert!.afterConfirm.pipe(tap(() => { closeReason = TmModalCloseReason.SUBMIT })),
        this.errorAlert!.afterCancel.pipe(tap(() => { closeReason = TmModalCloseReason.CLOSE })),
      ).pipe(take(1))
        .subscribe(() => {
          if (param.afterClose) {
            (param.afterClose as () => void)();
          }
          if (param.showCopyTip) {
            this.errorAlert!.showCopyButton = false;
          }
        });
      if (param.showCopyTip) {
        this.errorAlert!.showCopyButton = true;
      }
    }

    this.errorAlert!.open();
    return this.errorAlert as ErrorAlertComponent;
  }

  openIndeterminateProgressBar() {
    this.indeterminateProgressBar.show();
  }

  closeIndeterminateProgressBar() {
    this.indeterminateProgressBar.hide();
  }
}
